import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Save, Sparkles, X, Loader2 } from 'lucide-react';
import { adminApi, publicApi } from '../../api';
import { PageHeader, Table, Modal, Field, Input, Select, ImageUpload } from './AdminUI';

const EMPTY = {
  slug: '', title: '', subtitle: '', cover_image: '', body_md: '',
  activity_ids: [], city_slug: '', sponsored: false, published: true, order: 0
};

export default function AdminItineraries() {
  const [items, setItems] = useState([]);
  const [cities, setCities] = useState([]);
  const [activities, setActivities] = useState([]);
  const [editing, setEditing] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [actQuery, setActQuery] = useState('');

  const load = () => adminApi.itineraries().then(setItems);
  useEffect(() => {
    load();
    publicApi.cities().then(setCities);
    publicApi.activities().then(setActivities);
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!editing.slug || !editing.title) { alert('Slug e titolo obbligatori'); return; }
    const payload = {
      slug: editing.slug, title: editing.title, subtitle: editing.subtitle || null,
      cover_image: editing.cover_image || null, body_md: editing.body_md || null,
      activity_ids: editing.activity_ids || [], city_slug: editing.city_slug || null,
      sponsored: !!editing.sponsored, published: !!editing.published, order: Number(editing.order) || 0
    };
    if (editing.id) await adminApi.updateItinerary(editing.id, payload);
    else await adminApi.createItinerary(payload);
    setEditing(null); load();
  };

  const del = async (row) => {
    if (!window.confirm(`Eliminare "${row.title}"?`)) return;
    await adminApi.deleteItinerary(row.id); load();
  };

  const generateAI = async () => {
    if (!editing.activity_ids?.length) { alert('Seleziona almeno 1 attività'); return; }
    setGenerating(true);
    try {
      const res = await adminApi.generateItinerary({ activity_ids: editing.activity_ids, title: editing.title || 'Itinerario YUP', lang: 'it' });
      setEditing({ ...editing, body_md: (editing.body_md ? editing.body_md + '\n\n' : '') + res.body });
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore generazione AI');
    } finally { setGenerating(false); }
  };

  const toggleActivity = (id) => {
    const set = new Set(editing.activity_ids || []);
    set.has(id) ? set.delete(id) : set.add(id);
    setEditing({ ...editing, activity_ids: Array.from(set) });
  };

  const filteredActs = activities.filter(a =>
    !actQuery || a.name?.toLowerCase().includes(actQuery.toLowerCase())
  ).slice(0, 20);

  return (
    <div>
      <PageHeader title="Itinerari editoriali" subtitle={`${items.length} itinerari — mostrati in home per SEO ed engagement`}
        action={<button onClick={() => setEditing({...EMPTY})} className="btn-primary flex items-center gap-2"><Plus size={16}/> Nuovo</button>}
      />

      <Table
        columns={[
          { key: 'title', label: 'Titolo', render: r => <div><div className="font-semibold">{r.title}</div><div className="text-xs text-gray-500">/itinerari/{r.slug}</div></div> },
          { key: 'city_slug', label: 'Città', render: r => r.city_slug || 'Tutte' },
          { key: 'activity_ids', label: 'Tappe', render: r => (r.activity_ids || []).length },
          { key: 'sponsored', label: 'Sponsor', render: r => r.sponsored ? <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-900 text-xs font-bold">Sponsor</span> : '—' },
          { key: 'published', label: 'Stato', render: r => r.published ? <span className="text-green-700 text-xs font-semibold">Pubblicato</span> : <span className="text-gray-500 text-xs">Bozza</span> }
        ]}
        rows={items}
        actions={row => (
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing({...row})} className="p-1.5 hover:bg-gray-100 rounded text-blue-600"><Edit2 size={15}/></button>
            <button onClick={() => del(row)} className="p-1.5 hover:bg-gray-100 rounded text-red-600"><Trash2 size={15}/></button>
          </div>
        )}
      />

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'Modifica itinerario' : 'Nuovo itinerario'} maxWidth="max-w-3xl">
        {editing && (
          <form onSubmit={save}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Slug (URL) *"><Input required value={editing.slug} onChange={e => setEditing({...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')})} placeholder="aperitivo-al-tramonto"/></Field>
              <Field label="Titolo *"><Input required value={editing.title} onChange={e => setEditing({...editing, title: e.target.value})}/></Field>
            </div>
            <Field label="Sottotitolo"><Input value={editing.subtitle || ''} onChange={e => setEditing({...editing, subtitle: e.target.value})}/></Field>
            <Field label="Immagine di copertina"><ImageUpload value={editing.cover_image || ''} onChange={v => setEditing({...editing, cover_image: v})}/></Field>

            <Field label="Attività collegate">
              <input type="text" placeholder="Cerca attività per nome..." value={actQuery} onChange={e => setActQuery(e.target.value)}
                     className="w-full mb-2 px-3 py-2 border border-gray-300 rounded-md text-sm"/>
              <div className="mb-2 flex flex-wrap gap-1">
                {(editing.activity_ids || []).map(id => {
                  const a = activities.find(x => x.id === id);
                  return (
                    <span key={id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 text-blue-900 text-xs">
                      {a?.name || id}
                      <button type="button" onClick={() => toggleActivity(id)} className="hover:text-red-600"><X size={12}/></button>
                    </span>
                  );
                })}
              </div>
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md">
                {filteredActs.map(a => (
                  <label key={a.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 border-b border-gray-100 cursor-pointer text-sm">
                    <input type="checkbox" checked={(editing.activity_ids || []).includes(a.id)} onChange={() => toggleActivity(a.id)}/>
                    <span className="flex-1">{a.name}</span>
                    <span className="text-xs text-gray-400">{a.city_slug}</span>
                  </label>
                ))}
              </div>
            </Field>

            <Field label="Corpo articolo (Markdown)">
              <div className="flex justify-end mb-2">
                <button type="button" onClick={generateAI} disabled={generating || !editing.activity_ids?.length}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="ai-generate-btn">
                  {generating ? <><Loader2 size={12} className="animate-spin"/> Genero...</> : <><Sparkles size={12}/> Genera con AI</>}
                </button>
              </div>
              <textarea value={editing.body_md || ''} onChange={e => setEditing({...editing, body_md: e.target.value})}
                        rows={10} className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"/>
            </Field>

            <div className="grid grid-cols-3 gap-3">
              <Field label="Città">
                <Select value={editing.city_slug || ''} onChange={e => setEditing({...editing, city_slug: e.target.value})}>
                  <option value="">Tutte le città</option>
                  {cities.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </Select>
              </Field>
              <Field label="Ordine"><Input type="number" value={editing.order} onChange={e => setEditing({...editing, order: e.target.value})}/></Field>
              <Field label="Flag">
                <label className="flex items-center gap-2 text-xs mt-1"><input type="checkbox" checked={!!editing.published} onChange={e => setEditing({...editing, published: e.target.checked})}/> Pubblicato</label>
                <label className="flex items-center gap-2 text-xs mt-1"><input type="checkbox" checked={!!editing.sponsored} onChange={e => setEditing({...editing, sponsored: e.target.checked})}/> Sponsored</label>
              </Field>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-300 rounded-md">Annulla</button>
              <button type="submit" className="btn-primary flex items-center gap-2"><Save size={16}/> Salva</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
